import type { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

/**
 * Creates a new Dashboard entity, adds it to the dataspace tree, and opens a window.
 * Called both programmatically by the AI (via dashboard_create + dashboard_write) and
 * from the entity creation UI when the user picks "Dashboard" from the create menu.
 */
export async function dashboardCreate(params: {
  name?: string;
  description?: string;
  spaceId?: string;
  /**
   * When true, marks this dashboard as a v2 dynamic dashboard so the
   * DashboardApp enables its "Add widget" UI on open. AI-authored reports
   * continue to default to `dynamic: false` until the agent calls
   * `report_widget_upsert`, which bumps schemaVersion lazily.
   */
  dynamic?: boolean;
}): Promise<{ entityId: string | undefined; llmId: string | undefined }> {
  const {
    name = 'Dashboard',
    description,
    spaceId,
    dynamic = false,
  } = params;

  const entityStoreModule = await import('../../../stores/entityStore');
  const entityStore = entityStoreModule.default.getState();

  const now = new Date().toISOString();

  const newEntity = await entityStore.actions.createEntity({
    entityType: 'Dashboard',
    name: name.trim(),
    spaceId,
    skeleton: {
      status: 'generating',
      description: description || '',
      generatedAt: now,
      base64: '',
      dynamic,
      schemaVersion: dynamic ? 2 : undefined,
    },
    created_at: now,
  } as any);

  const entityId = newEntity._id || newEntity.id;
  const llmId = newEntity.llmId;

  if (llmId && entityId) {
    const { llmIdMapper } = await import('../../../utils/llmIdMapper');
    llmIdMapper.registerMapping(llmId, entityId, 'document', 'Dashboard', newEntity.name);
  }

  // Add to dataspace tree
  if (entityId && spaceId) {
    try {
      const { default: useDataspaceTreeStore } = await import('../../../stores/dataspaceTreeStore');
      useDataspaceTreeStore.getState()._applyAddNode(`${spaceId}:${entityId}`, {
        id: entityId,
        parentId: spaceId,
        pos: '!',
        expanded: false,
        kind: 'canonical',
      });
    } catch (treeError) {
      console.warn('[dashboard_create] Failed to add to tree (non-fatal):', treeError);
    }
  }

  // Open window
  try {
    const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
    await useCommandRegistryStore.getState().executeCommand('space_open_window', {
      entityId,
    });
  } catch (windowError) {
    console.error('[dashboard_create] Failed to open window:', windowError);
  }

  return { entityId, llmId };
}

// ---------------------------------------------------------------------------
// SelfContainedCommand — registered in commandRegistryStore so the sidebar
// "Add widget" path can call executeCommand('dashboard_create', {...}).
// ---------------------------------------------------------------------------

export const dashboardCreateCommand: SelfContainedCommand = {
  id: 'dashboard_create',
  description: 'Create a new Dashboard entity and open it.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Name for the dashboard (default: "Dashboard")',
        required: false,
        defaultValue: 'Dashboard',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space to create the dashboard in',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder to place the dashboard in',
        required: false,
      },
      {
        name: 'dynamic',
        type: 'boolean',
        description: 'Mark as a v2 dynamic dashboard (enables live widgets)',
        required: false,
        defaultValue: false,
      },
    ],
  },

  examples: { good: [], bad: [] },
  performance: { expectedDuration: 500, complexity: 'low' },

  handler: async (params: any, context: CommandExecutionContext) => {
    const startTime = Date.now();
    const { name = 'Dashboard', spaceId: providedSpaceId, dynamic = false } = params;
    const spaceId = providedSpaceId || context.spaceId;

    try {
      const result = await dashboardCreate({ name, spaceId, dynamic });

      if (!result.entityId) {
        return createErrorResult('Failed to create dashboard', 'Entity creation returned no id', 'execution');
      }

      return createSuccessResult(
        `Dashboard "${name}" created`,
        { entityId: result.entityId, llmId: result.llmId, name },
        { metadata: { executionTime: Date.now() - startTime } } as any,
      );
    } catch (err: any) {
      return createErrorResult(
        'Failed to create dashboard',
        err.message || String(err),
        'execution',
        { metadata: { executionTime: Date.now() - startTime } },
      );
    }
  },
};

// Register so the entity creation UI shows "Dashboard" as an option
entityCreationRegistry.register({
  commandId: 'dashboard_create',
  entityType: 'Dashboard',
  displayName: 'Dashboard',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'ChartBar',
    phosphorWeight: 'duotone',
  },
  description: 'AI-generated report with metrics, charts, and tables',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'document',
  sortOrder: 5,
});
